<script setup>
import { ref, computed, onMounted } from 'vue';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { subDays } from 'date-fns';
import { getUnixStartOfDay, getUnixEndOfDay } from 'helpers/DateHelper';
import ReportsAPI from 'dashboard/api/reports';
import WootDatePicker from 'dashboard/components/ui/DatePicker/DatePicker.vue';
import { DATE_RANGE_TYPES } from 'dashboard/components/ui/DatePicker/helpers/DatePickerHelper';
import ReportHeader from './ReportHeader.vue';
import Spinner from 'shared/components/Spinner.vue';

const store = useStore();
const inboxes = useMapGetter('inboxes/getInboxes');

const dateMode = ref('now');
const customDateRange = ref([subDays(new Date(), 6), new Date()]);
const selectedDateRange = ref(DATE_RANGE_TYPES.LAST_7_DAYS);
const inboxId = ref(null);
const isLoading = ref(false);
const hasError = ref(false);
const report = ref({ agents: [] });

const since = computed(() =>
  dateMode.value === 'range'
    ? getUnixStartOfDay(customDateRange.value[0])
    : undefined
);
const until = computed(() =>
  dateMode.value === 'range'
    ? getUnixEndOfDay(customDateRange.value[1])
    : undefined
);

// Columns rendered after the agent name, in order.
const COLUMNS = [
  'total',
  'unattended',
  'customer',
  'agent',
  'automation',
  'follow_up',
  'unknown',
];

// Columns explained in the legend (everything except the total).
const LEGEND_COLUMNS = [
  'unattended',
  'customer',
  'agent',
  'automation',
  'follow_up',
  'unknown',
];

const num = value => Number(value || 0);

const agents = computed(() => report.value.agents || []);

const totalsRow = computed(() => {
  const totals = {};
  COLUMNS.forEach(column => {
    totals[column] = 0;
  });
  agents.value.forEach(agent => {
    COLUMNS.forEach(column => {
      totals[column] += num(agent[column]);
    });
  });
  return totals;
});

let requestId = 0;
const fetchReport = async () => {
  requestId += 1;
  const currentRequest = requestId;
  isLoading.value = true;
  hasError.value = false;
  try {
    const { data } = await ReportsAPI.getAgentOriginStats({
      since: since.value,
      until: until.value,
      inboxId: inboxId.value || undefined,
    });
    if (currentRequest !== requestId) return;
    report.value = {
      agents: data?.agents || [],
    };
  } catch (error) {
    if (currentRequest !== requestId) return;
    hasError.value = true;
    if (currentRequest !== requestId) return;
    report.value = { agents: [] };
  } finally {
    if (currentRequest === requestId) isLoading.value = false;
  }
};

const setDateMode = mode => {
  if (dateMode.value === mode) return;
  dateMode.value = mode;
  fetchReport();
};

const onDateRangeChange = ([startDate, endDate, rangeType]) => {
  customDateRange.value = [startDate, endDate];
  selectedDateRange.value = rangeType || DATE_RANGE_TYPES.CUSTOM_RANGE;
  fetchReport();
};

const onInboxChange = () => {
  fetchReport();
};

onMounted(() => {
  store.dispatch('inboxes/get');
  fetchReport();
});
</script>

<template>
  <ReportHeader :header-title="$t('REPORT.AGENT_ORIGIN.TITLE')" />
  <p class="px-4 text-xs text-n-slate-11">
    {{ $t('REPORT.CAMPAIGN_PERFORMANCE.AGENT_SEMANTICS') }}
  </p>
  <div class="flex flex-col flex-1 gap-6">
    <div
      class="flex flex-col flex-wrap w-full gap-3 md:flex-row md:items-center"
    >
      <div
        class="inline-flex rounded-lg border border-n-weak bg-n-alpha-black2"
      >
        <button
          type="button"
          class="px-3 py-2 text-sm rounded-l-lg"
          :class="
            dateMode === 'now'
              ? 'bg-n-brand text-white'
              : 'text-n-slate-11 hover:text-n-slate-12'
          "
          @click="setDateMode('now')"
        >
          {{ $t('REPORT.AGENT_ORIGIN.DATE_MODE_NOW') }}
        </button>
        <button
          type="button"
          class="px-3 py-2 text-sm rounded-r-lg"
          :class="
            dateMode === 'range'
              ? 'bg-n-brand text-white'
              : 'text-n-slate-11 hover:text-n-slate-12'
          "
          @click="setDateMode('range')"
        >
          {{ $t('REPORT.AGENT_ORIGIN.DATE_MODE_RANGE') }}
        </button>
      </div>

      <WootDatePicker
        v-if="dateMode === 'range'"
        v-model:date-range="customDateRange"
        v-model:range-type="selectedDateRange"
        @date-range-changed="onDateRangeChange"
      />

      <select
        v-model="inboxId"
        class="w-full md:w-60 mb-0 text-sm rounded-lg border border-n-weak bg-n-alpha-black2 text-n-slate-12"
        @change="onInboxChange"
      >
        <option :value="null">
          {{ $t('REPORT.AGENT_ORIGIN.ALL_INBOXES') }}
        </option>
        <option v-for="inbox in inboxes" :key="inbox.id" :value="inbox.id">
          {{ inbox.name }}
        </option>
      </select>
    </div>

    <div
      v-if="hasError"
      class="p-4 text-sm border rounded-lg border-n-ruby-6 bg-n-ruby-2 text-n-ruby-11"
    >
      {{ $t('REPORT.AGENT_ORIGIN.ERROR') }}
    </div>

    <div v-if="isLoading" class="flex items-center justify-center py-10">
      <Spinner />
    </div>

    <template v-else>
      <div class="overflow-x-auto border rounded-lg border-n-weak">
        <table class="w-full text-sm text-left">
          <thead class="text-n-slate-11 bg-n-alpha-black2">
            <tr>
              <th class="px-4 py-3 font-medium whitespace-nowrap">
                {{ $t('REPORT.AGENT_ORIGIN.TABLE.AGENT') }}
              </th>
              <th
                v-for="column in COLUMNS"
                :key="column"
                class="px-4 py-3 font-medium text-right whitespace-nowrap"
                :class="{ 'text-n-ruby-11': column === 'unattended' }"
              >
                {{ $t(`REPORT.AGENT_ORIGIN.COLUMNS.${column}`) }}
              </th>
            </tr>
          </thead>
          <tbody class="divide-y divide-n-weak">
            <tr v-if="!agents.length">
              <td
                :colspan="COLUMNS.length + 1"
                class="px-4 py-6 text-center text-n-slate-11"
              >
                {{ $t('REPORT.AGENT_ORIGIN.TABLE.NO_DATA') }}
              </td>
            </tr>
            <tr
              v-for="agent in agents"
              :key="agent.id ?? 'unassigned'"
              class="text-n-slate-12"
              :class="{ 'italic text-n-slate-11': agent.id === null }"
            >
              <td class="px-4 py-3 whitespace-nowrap">
                {{ agent.name }}
              </td>
              <td
                v-for="column in COLUMNS"
                :key="column"
                class="px-4 py-3 text-right tabular-nums"
                :class="{ 'text-n-ruby-11': column === 'unattended' }"
              >
                {{ num(agent[column]) }}
              </td>
            </tr>
          </tbody>
          <tfoot
            v-if="agents.length"
            class="font-medium border-t-2 border-n-weak text-n-slate-12 bg-n-alpha-black2"
          >
            <tr>
              <td class="px-4 py-3 whitespace-nowrap">
                {{ $t('REPORT.AGENT_ORIGIN.COLUMNS.total') }}
              </td>
              <td
                v-for="column in COLUMNS"
                :key="column"
                class="px-4 py-3 text-right tabular-nums"
                :class="{ 'text-n-ruby-11': column === 'unattended' }"
              >
                {{ num(totalsRow[column]) }}
              </td>
            </tr>
          </tfoot>
        </table>
      </div>

      <div class="flex flex-col gap-2 p-4 border rounded-lg border-n-weak">
        <h3 class="m-0 text-sm font-medium text-n-slate-12">
          {{ $t('REPORT.AGENT_ORIGIN.LEGEND.HEADING') }}
        </h3>
        <ul class="flex flex-col gap-1 m-0 text-sm text-n-slate-11">
          <li v-for="column in LEGEND_COLUMNS" :key="column">
            <span class="font-medium text-n-slate-12">
              {{ $t(`REPORT.AGENT_ORIGIN.COLUMNS.${column}`) }}:
            </span>
            {{ $t(`REPORT.AGENT_ORIGIN.LEGEND.${column}`) }}
          </li>
        </ul>
      </div>
    </template>
  </div>
</template>
